import React from 'react';
import { formatCurrency, formatPercent } from '@shared';

interface TraditionalLTCQuoteComparisonProps {
  quote: TraditionalLTCQuote;
  expectedROI: number;
}

interface QuoteFieldConfig {
  key: string;
  label: string | ((quote: TraditionalLTCQuote) => string);
  getValue: (quoteData: any) => any;
  formatter: (value: any) => string;
}

interface QuoteColumnProps {
  title: string;
  titleClassName: string;
  fields: QuoteFieldConfig[];
  quoteData: any;
  quote: TraditionalLTCQuote;
  valueClassName: string;
  valueEmphasisClassName: string;
}

const formatPercentage = (value: number) => formatPercent(value / 100, 1);

const QuoteColumn: React.FC<QuoteColumnProps> = ({
  title,
  titleClassName,
  fields,
  quoteData,
  quote,
  valueClassName,
  valueEmphasisClassName,
}) => {
  return (
    <div className="space-y-2">
      <p className={`text-xs font-medium ${titleClassName}`}>{title}</p>
      <div className="space-y-1">
        {fields.map((field) => {
          const label = typeof field.label === 'function' ? field.label(quote) : field.label;
          const value = field.getValue(quoteData);
          
          return (
            <p key={field.key} className={`text-sm ${valueClassName}`}>
              {label}: <span className={valueEmphasisClassName}>{field.formatter(value)}</span>
            </p>
          );
        })}
      </div>
    </div>
  );
};

export const TraditionalLTCQuoteComparison: React.FC<TraditionalLTCQuoteComparisonProps> = ({
  quote,
  expectedROI,
}) => {
  const premiumLabel = quote.premiumFrequency === 'Monthly' 
    ? 'Monthly Premium' 
    : 'Annual Premium';

  const quoteData = {
    premium: Math.round(quote.primaryModalPremium),
    premiumLabel,
    yearlyPremium: Math.round(quote.primaryTotalYearlyPremium),
    dailyBenefit: Math.round(quote.dailyBenefitAmount),
    benefitPool: Math.round(quote.benefitPool),
    eliminationPeriod: quote.eliminationPeriod,
    bioPercent: quote.bioPercent,
    expectedROI: expectedROI * 100, // Convert to percentage
  };

  const fields: QuoteFieldConfig[] = [
    {
      key: 'premium',
      label: (q) => q.premiumFrequency === 'Monthly' ? 'Monthly Premium' : 'Annual Premium',
      getValue: (data) => data.premium,
      formatter: formatCurrency,
    },
    {
      key: 'yearlyPremium',
      label: 'Yearly Premium',
      getValue: (data) => data.yearlyPremium,
      formatter: formatCurrency,
    },
    {
      key: 'dailyBenefit',
      label: 'Daily Benefit',
      getValue: (data) => data.dailyBenefit,
      formatter: formatCurrency,
    },
    {
      key: 'benefitPool',
      label: 'Benefit Pool',
      getValue: (data) => data.benefitPool,
      formatter: formatCurrency,
    },
    {
      key: 'eliminationPeriod',
      label: 'Elimination Period',
      getValue: (data) => data.eliminationPeriod,
      formatter: (value) => `${value} days`,
    },
    {
      key: 'bioPercent',
      label: 'Benefit Inflation Option',
      getValue: (data) => data.bioPercent,
      formatter: (value) => value,
    },
    {
      key: 'expectedROI',
      label: 'Expected ROI',
      getValue: (data) => data.expectedROI,
      formatter: formatPercentage,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 rounded-lg bg-white p-4">
      <QuoteColumn
        title="Initial Estimate"
        titleClassName="text-gray-500"
        fields={fields}
        quoteData={quoteData}
        quote={quote}
        valueClassName="text-gray-600"
        valueEmphasisClassName="font-semibold"
      />
      <QuoteColumn
        title="Updated Quote"
        titleClassName="text-primary"
        fields={fields}
        quoteData={quoteData}
        quote={quote}
        valueClassName="text-gray-900"
        valueEmphasisClassName="font-bold text-primary"
      />
    </div>
  );
};